import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthState, useAuth } from "../../context/AuthContext";
import { getUserProfile } from "../../api/userApi";
import customFetch from "../../api/customFetch";
import SurveyButton from '../../components/SurveyButton';

import {
  DashboardContainer,
  Header,
  HeaderContent,
  UserSection,
  Avatar,
  UserInfo,
  Greeting,
  WelcomeText,
  SectorBadge,
  HeaderActions,
  Button,
  StatsContainer,
  StatCard,
  StatNumber,
  StatLabel,
  ProgressSection,
  ProgressRing,
  RingText,
  RingNumber,
  RingLabel,
  ProgressDetails,
  PendingMessage,
  Tabs,
  Tab,
  CoursesSection,
  SectionTitle,
  CourseGrid,
  CourseCard,
  CourseStatus,
  CourseTitle,
  CourseDescription,
  ProgressBar,
  ProgressFill,
  ProgressText,
  CourseMeta,
  MetaItem,
  ActionButton,
  ActionButtons,
  EmptyState,
  EmptyIcon,
  ExamStats,
  ExamStat,
  ExamStatNumber,
  ExamStatLabel,
  MaxAttempts,
  LoadingMessage,
} from "./styles.js";

export const loader = async () => {
  try {
    const response = await customFetch.get("users/profile");
    return response.data;
  } catch (error) {
    return error;
  }
};

const UserDashboard = () => {
  const [courses, setCourses] = useState([]);
  const [exams, setExams] = useState([]);
  const [courseStats, setCourseStats] = useState({
    total: 0, completed: 0, inProgress: 0, pending: 0, completionRate: 0
  });
  const [examStats, setExamStats] = useState({
    total: 0, completed: 0, failed: 0, pending: 0, passRate: 0
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('courses');
  
  const { isLoggedIn, user, loading: authLoading } = useAuthState();
  const { logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading) return;
    if (!isLoggedIn) {
      navigate("/user/login");
      return;
    }
    fetchUserData();
  }, [navigate, isLoggedIn, authLoading]);

  const fetchUserData = async () => {
    try {
      const response = await getUserProfile();
      if (response.success) {
        setCourses(response.user.courses || []);
        setExams(response.user.exams || []);
        setCourseStats(response.user.courseStats || courseStats);
        setExamStats(response.user.examStats || examStats);
      } else {
        await logout();
        navigate("/user/login");
      }
    } catch (error) {
      await logout();
      navigate("/user/login");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/user/login");
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'בוקר טוב';
    if (hour < 17) return 'צהריים טובים';
    if (hour < 21) return 'ערב טוב';
    return 'לילה טוב';
  };

  const getStatusText = (status) => {
    const map = { pending: 'ממתין', in_progress: 'בתהליך', completed: 'הושלם', failed: 'נכשל' };
    return map[status] || status;
  };

  const getStatusColor = (status) => {
    const map = { pending: '#f39c12', in_progress: '#3498db', completed: '#27ae60', failed: '#e74c3c' };
    return map[status] || '#95a5a6';
  };

  if (authLoading || loading) {
    return (
      <LoadingMessage>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', marginBottom: '16px' }}>⏳</div>
          <p>טוען את הנתונים שלך...</p>
        </div>
      </LoadingMessage>
    );
  }

  if (!isLoggedIn) return null;

  return (
    <DashboardContainer>
      {/* Header */}
      <Header>
        <HeaderContent>
          <UserSection>
            <Avatar>{user?.fullName?.charAt(0) || 'מ'}</Avatar>
            <UserInfo>
              <Greeting>{getGreeting()},</Greeting>
              <WelcomeText>{user?.fullName || user?.username}</WelcomeText>
              <SectorBadge>{user?.sector}</SectorBadge>
            </UserInfo>
          </UserSection>
          <HeaderActions>
            {(user?.role === 'admin' || user?.role === 'org_manager') && (
              <Button variant='secondary' onClick={() => navigate('/admin')}>
                ⚙️ ניהול
              </Button>
            )}
            {user?.role === 'sector_manager' && (
              <Button
                variant='secondary'
                onClick={() => navigate('/manager/dashboard')}
              >
                📊 דשבורד מנהל
              </Button>
            )}
            <Button variant='danger' onClick={handleLogout}>
              🚪 התנתק
            </Button>
          </HeaderActions>
        </HeaderContent>
      </Header>

      {/* Stats */}
      <StatsContainer>
        <StatCard>
          <StatNumber>{courseStats.total}</StatNumber>
          <StatLabel>📚 סה"כ לומדות</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber style={{ color: '#27ae60' }}>
            {courseStats.completed}
          </StatNumber>
          <StatLabel>✅ הושלמו</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber style={{ color: '#f39c12' }}>
            {courseStats.inProgress}
          </StatNumber>
          <StatLabel>⏳ בתהליך</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber style={{ color: '#3498db' }}>
            {courseStats.completionRate}%
          </StatNumber>
          <StatLabel>🎯 אחוז השלמה</StatLabel>
        </StatCard>
      </StatsContainer>

      {/* Progress Ring */}
      <ProgressSection>
        <ProgressRing>
          <svg viewBox='0 0 100 100'>
            <defs>
              <linearGradient id='gradient' x1='0%' y1='0%' x2='100%' y2='0%'>
                <stop offset='0%' stopColor='#667eea' />
                <stop offset='100%' stopColor='#764ba2' />
              </linearGradient>
            </defs>
            <circle className='ring-bg' cx='50' cy='50' r='40' />
            <circle
              className='ring-fill'
              cx='50'
              cy='50'
              r='40'
              strokeDasharray={`${courseStats.completionRate * 2.51} 251`}
            />
          </svg>
          <RingText>
            <RingNumber>{courseStats.completionRate}%</RingNumber>
            <RingLabel>הושלם</RingLabel>
          </RingText>
        </ProgressRing>
        <ProgressDetails>
          <h3>ההתקדמות שלך</h3>
          <p>
            השלמת {courseStats.completed} מתוך {courseStats.total} לומדות
          </p>
          {courseStats.pending > 0 && (
            <PendingMessage>
              נותרו לך עוד {courseStats.pending} לומדות להשלים
            </PendingMessage>
          )}
        </ProgressDetails>
      </ProgressSection>

      {/* Tabs */}
      <Tabs>
      <p>פעולות</p>
        <SurveyButton />
        <Button variant='secondary' onClick={() => navigate('/regulations')} style={{ marginBottom: 8 }}>
          📋 תקנונים
        </Button>
        <Button variant='secondary' onClick={() => navigate('/procedures')} style={{ marginBottom: 8 }}>
          📑 נהלי עבודה
        </Button>
        <Tab
          active={activeTab === 'courses'}
          onClick={() => setActiveTab('courses')}
        >
          📚 לומדות ({courses.length})
        </Tab>
        <Tab
          active={activeTab === 'exams'}
          onClick={() => setActiveTab('exams')}
        >
          📝 מבחנים ({exams.length})
        </Tab>
      </Tabs>

      {/* Courses Tab */}
      {activeTab === 'courses' && (
        <CoursesSection>
          <SectionTitle>הלומדות שלי</SectionTitle>
          {courses.length === 0 ? (
            <EmptyState>
              <EmptyIcon>📭</EmptyIcon>
              <h3>אין לומדות זמינות</h3>
              <p style={{ color: '#666' }}>כרגע אין לומדות מוקצות עבורך</p>
            </EmptyState>
          ) : (
            <CourseGrid>
              {courses.map((course) => (
                <CourseCard key={course.courseId._id} status={course.status}>
                  <CourseStatus color={getStatusColor(course.status)}>
                    {getStatusText(course.status)}
                  </CourseStatus>
                  <CourseTitle>{course.courseId.title}</CourseTitle>
                  <CourseDescription>
                    {course.courseId.description}
                  </CourseDescription>

                  <ProgressBar>
                    <ProgressFill width={course.progress} />
                  </ProgressBar>
                  <ProgressText>
                    {course.progress}% הושלם
                    {course.score != null && ` | ציון: ${course.score}%`}
                  </ProgressText>

                  <CourseMeta>
                    {course.courseId.estimatedDuration && (
                      <MetaItem>
                        ⏱️ {course.courseId.estimatedDuration} דק'
                      </MetaItem>
                    )}
                    {course.attempts > 0 && (
                      <MetaItem>🔄 {course.attempts} ניסיונות</MetaItem>
                    )}
                  </CourseMeta>

                  {course.status === 'pending' && (
                    <ActionButton
                      onClick={() => navigate(`/${course.courseId.slug}`)}
                    >
                      התחל לומדה
                    </ActionButton>
                  )}
                  {course.status === 'in_progress' && (
                    <ActionButton
                      onClick={() => navigate(`/${course.courseId.slug}`)}
                    >
                      המשך לימוד
                    </ActionButton>
                  )}
                  {course.status === 'completed' && (
                    <ActionButtons columns='1fr 1fr'>
                      <Button
                        variant='success'
                        onClick={() =>
                          navigate(`/${course.courseId.slug}/results`)
                        }
                      >
                        צפה בתוצאות
                      </Button>
                      <Button
                        variant='secondary'
                        onClick={() => navigate(`/${course.courseId.slug}`)}
                      >
                        בצע שוב
                      </Button>
                    </ActionButtons>
                  )}
                </CourseCard>
              ))}
            </CourseGrid>
          )}
        </CoursesSection>
      )}

      {/* Exams Tab */}
      {activeTab === 'exams' && (
        <>
          <ExamStats>
            <ExamStat>
              <ExamStatNumber>{examStats.total}</ExamStatNumber>
              <ExamStatLabel>סה"כ</ExamStatLabel>
            </ExamStat>
            <ExamStat>
              <ExamStatNumber color='#27ae60'>
                {examStats.completed}
              </ExamStatNumber>
              <ExamStatLabel>עברו</ExamStatLabel>
            </ExamStat>
            <ExamStat>
              <ExamStatNumber color='#e74c3c'>
                {examStats.failed}
              </ExamStatNumber>
              <ExamStatLabel>נכשלו</ExamStatLabel>
            </ExamStat>
            <ExamStat>
              <ExamStatNumber color='#667eea'>
                {examStats.passRate}%
              </ExamStatNumber>
              <ExamStatLabel>הצלחה</ExamStatLabel>
            </ExamStat>
          </ExamStats>

          <CoursesSection>
            <SectionTitle>המבחנים שלי</SectionTitle>
            {exams.length === 0 ? (
              <EmptyState>
                <EmptyIcon>📭</EmptyIcon>
                <h3>אין מבחנים זמינים</h3>
                <p style={{ color: '#666' }}>כרגע אין מבחנים מוקצים עבורך</p>
              </EmptyState>
            ) : (
              <CourseGrid>
                {exams.map((exam) => (
                  <CourseCard key={exam.examId._id} status={exam.status}>
                    <CourseStatus color={getStatusColor(exam.status)}>
                      {exam.status === 'completed'
                        ? 'עבר'
                        : exam.status === 'failed'
                        ? 'נכשל'
                        : 'ממתין'}
                    </CourseStatus>
                    <CourseTitle>{exam.examId.title}</CourseTitle>
                    <CourseDescription>
                      {exam.examId.description}
                    </CourseDescription>

                    <ProgressBar>
                      <ProgressFill
                        width={exam.progress}
                        style={{
                          background: exam.status === 'failed' ? '#e74c3c' : '',
                        }}
                      />
                    </ProgressBar>
                    <ProgressText>
                      ניסיונות: {exam.attempts}/{exam.examId.maxAttempts}
                      {exam.score != null && ` | ציון: ${exam.score}%`}
                    </ProgressText>

                    <CourseMeta>
                      {exam.examId.timeLimit && (
                        <MetaItem>⏱️ {exam.examId.timeLimit} דק'</MetaItem>
                      )}
                      <MetaItem>🎯 עובר: {exam.examId.passScore}%</MetaItem>
                    </CourseMeta>

                    {exam.status === 'pending' && !exam.maxAttemptsReached && (
                      <ActionButton
                        onClick={() => navigate(`/exams/${exam.examId.slug}`)}
                      >
                        התחל מבחן
                      </ActionButton>
                    )}
                    {exam.status === 'failed' && !exam.maxAttemptsReached && (
                      <Button
                        variant='warning'
                        style={{ width: '100%', justifyContent: 'center' }}
                        onClick={() => navigate(`/exams/${exam.examId.slug}`)}
                      >
                        נסה שוב ({exam.examId.maxAttempts - exam.attempts}{' '}
                        נותרו)
                      </Button>
                    )}
                    {exam.maxAttemptsReached && exam.status !== 'completed' && (
                      <MaxAttempts>הגעת למקסימום ניסיונות</MaxAttempts>
                    )}
                    {exam.status === 'completed' && (
                      <ActionButtons
                        columns={exam.maxAttemptsReached ? '1fr' : '1fr 1fr'}
                      >
                        <Button
                          variant='success'
                          onClick={() =>
                            navigate(`/exams/${exam.examId.slug}/results`)
                          }
                        >
                          צפה בתוצאות
                        </Button>
                        {!exam.maxAttemptsReached && (
                          <Button
                            variant='secondary'
                            onClick={() =>
                              navigate(`/exams/${exam.examId.slug}`)
                            }
                          >
                            בצע שוב
                          </Button>
                        )}
                      </ActionButtons>
                    )}
                  </CourseCard>
                ))}
              </CourseGrid>
            )}
          </CoursesSection>
        </>
      )}
    </DashboardContainer>
  );
};

export default UserDashboard;